import { useContext } from 'react';
import cookie from 'react-cookies';

import { SurveyCtx } from '@r1-frontend/survey/context';
import { setContextSurvey, start } from '@r1-frontend/survey/context/actions';
import { SURVEY_COOKIE, SURVEY_COOKIE_CONFIG } from '@r1-frontend/survey/context/constants';
import { retrieveSurvey } from '@r1-frontend/survey/context/helpers/retrieveSurvey';
import { useCtxDispatch } from '@r1-frontend/survey/context/hooks/index';
import { TShowDetails } from '@r1-frontend/survey/context/reducer/initialState';
import { SurveyType } from '@r1-frontend/survey/context/surveys';

/**
 * Хук, предоставляющий методы для работы с опросами
 *
 * @todo тесты на хук
 *
 * @returns {{
 *  register: (name: string) => void;
 *  show: (data: TShowDetails) => void;
 *  markAsCompleted: (type: SurveyType, name: string, activeTo?: Date) => void;
 *  isComplete: (name: string) => boolean;
 * }}
 */
const useSurvey = (): {
    register: (name: string) => void,
    show: (data: TShowDetails) => void,
    markAsCompleted: (type: SurveyType, name: string, activeTo?: Date) => void,
    isComplete: (name: string) => boolean,
} => {
    const { completedSurveys } = useContext(SurveyCtx);
    const dispatch = useCtxDispatch();

    /**
     * Регистрация контекстного опроса
     *
     * @param {string} name - название контекстного опроса
     */
    const register = (name: string) => {
        const contextSurvey = retrieveSurvey(name);

        if (contextSurvey && 'name' in contextSurvey) {
            dispatch(setContextSurvey(contextSurvey));
        }
    };

    /**
     * Отображение нужного опроса в попапе
     *
     * @param {import('../surveys').SurveyType} type - тип опроса ('common' или 'context')
     * @param {?string} [name] - название опроса
     */
    const show = (data: TShowDetails) => {
        dispatch(start(data));
    };

    /**
     * Помечает опрос пройденным
     *
     * @param {import('../surveys').SurveyType} type - тип опроса ('common' или 'context')
     * @param {string} name - название опроса
     * @param {?Date} [activeTo] - дата окончания проведения опроса
     */
    const markAsCompleted = (type: SurveyType, name: string, activeTo?: Date) => {
        // тип значения куки: { common: string, context: string[] }
        let updated = {};

        if (Object.keys(completedSurveys).length) {
            if (type in completedSurveys) {
                updated = {
                    ...completedSurveys,
                    [type]: Array.isArray(completedSurveys[type]) ? [...completedSurveys[type], name] : completedSurveys[type],
                };
            } else {
                updated = {
                    ...completedSurveys,
                    [type]: type === 'common' ? name : [name],
                };
            }
        } else {
            updated = {
                [type]: type === 'common' ? name : [name],
            };
        }

        cookie.save(SURVEY_COOKIE, updated, { ...SURVEY_COOKIE_CONFIG, expires: activeTo });
    };

    /**
     * Проверяет, является ли опрос уже пройденным
     *
     * @param {string} name - название опроса
     * @returns {boolean} результат проверки
     */
    const isComplete = (name: string) => {
        if (!Object.keys(completedSurveys).length) {
            return false;
        }

        return Object
            .values(completedSurveys)
            .some(completed => Array.isArray(completed) ? completed.includes(name) : completed === name);
    };

    return {
        register,
        show,
        markAsCompleted,
        isComplete,
    };
};

export default useSurvey;
